/**
 * AiD Sessions - Security Sessions Management Component
 *
 * Allows users to view and manage their active authentication sessions.
 * Features:
 * - List all active refresh token sessions
 * - Revoke individual sessions
 * - "Revoke all other sessions" for security
 *
 * Uses the authFetch wrapper for automatic token management.
 */

import { useEffect, useState, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { authFetch } from '../lib/authClient'
import { useLocale } from '../hooks/useLocale'
import {
  Shield,
  Smartphone,
  Monitor,
  Trash2,
  AlertTriangle,
  RefreshCw,
  Check,
  X,
} from 'lucide-react'

const API_BASE_URL = import.meta.env.VITE_AUTH_API_URL || 'https://api.lukhas.id/v1'

interface Session {
  id: string
  created_at: string | null
  expires_at: string | null
  revoked: boolean
}

interface AidSessionsProps {
  className?: string
}

export default function AidSessions({ className = '' }: AidSessionsProps) {
  const { t } = useLocale()
  const [sessions, setSessions] = useState<Session[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [confirmRevoke, setConfirmRevoke] = useState<string | null>(null)

  /**
   * Load sessions from server
   */
  const loadSessions = useCallback(async () => {
    setError(null)
    setLoading(true)
    try {
      const resp = await authFetch(`${API_BASE_URL}/identity/sessions`, {
        method: 'GET',
      })

      if (!resp.ok) {
        const err = await resp.json().catch(() => ({ detail: 'Failed to load sessions' }))
        throw new Error(err.detail || 'Failed to load sessions')
      }

      const body = await resp.json()
      setSessions(body.sessions || [])
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load sessions'
      setError(message)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadSessions()
  }, [loadSessions])

  /**
   * Revoke a single session
   */
  async function revokeSession(sessionId: string) {
    setBusyId(sessionId)
    setError(null)
    try {
      const resp = await authFetch(
        `${API_BASE_URL}/identity/sessions/${sessionId}/revoke`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        }
      )

      if (!resp.ok) {
        const err = await resp.json().catch(() => ({ detail: 'Failed to revoke' }))
        throw new Error(err.detail || 'Failed to revoke session')
      }

      // Update local state
      setSessions(prev =>
        prev.map(s => (s.id === sessionId ? { ...s, revoked: true } : s))
      )
      setConfirmRevoke(null)
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to revoke session'
      setError(message)
    } finally {
      setBusyId(null)
    }
  }

  /**
   * Revoke all other sessions
   */
  async function revokeOtherSessions() {
    setBusyId('revoke-others')
    setError(null)
    try {
      const resp = await authFetch(`${API_BASE_URL}/identity/sessions/revoke-others`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ except_current: true }),
      })

      if (!resp.ok) {
        const err = await resp.json().catch(() => ({ detail: 'Failed to revoke' }))
        throw new Error(err.detail || 'Failed to revoke sessions')
      }

      // Reload sessions to get updated state
      await loadSessions()
      setConfirmRevoke(null)
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to revoke sessions'
      setError(message)
    } finally {
      setBusyId(null)
    }
  }

  /**
   * Get device icon based on session metadata (placeholder - would need UA parsing)
   */
  function getDeviceIcon() {
    // In production, parse user agent to show appropriate icon
    return <Monitor className="w-5 h-5 text-white/40" />
  }

  /**
   * Format relative time
   */
  function formatRelativeTime(dateStr: string | null): string {
    if (!dateStr) return 'Unknown'
    try {
      const date = new Date(dateStr)
      const now = new Date()
      const diffMs = now.getTime() - date.getTime()
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

      if (diffDays === 0) return 'Today'
      if (diffDays === 1) return 'Yesterday'
      if (diffDays < 7) return `${diffDays} days ago`
      if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`
      return date.toLocaleDateString()
    } catch {
      return 'Unknown'
    }
  }

  const activeSessions = sessions.filter(s => !s.revoked)
  const revokedSessions = sessions.filter(s => s.revoked)

  return (
    <div className={`space-y-6 ${className}`} data-testid="aid-sessions-list">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Shield className="w-5 h-5 text-white/40" />
          <span className="text-xs text-white/30 uppercase tracking-widest">
            Active Sessions
          </span>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={loadSessions}
            disabled={loading}
            className="p-2 text-white/30 hover:text-white/60 transition-colors duration-300 disabled:opacity-50"
            aria-label="Refresh sessions"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>

          {activeSessions.length > 1 && (
            <button
              onClick={() => setConfirmRevoke('all-others')}
              disabled={!!busyId}
              className="flex items-center gap-2 px-3 py-1.5 text-xs text-white/50 hover:text-white/80 border border-white/10 hover:border-white/20 transition-all duration-300 disabled:opacity-50"
            >
              <AlertTriangle className="w-3 h-3" />
              Sign out other devices
            </button>
          )}
        </div>
      </div>

      {/* Error display */}
      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-3 bg-red-500/10 border border-red-500/20 text-red-400 text-sm"
        >
          {error}
        </motion.div>
      )}

      {/* Loading state */}
      {loading && sessions.length === 0 && (
        <div className="text-center py-8">
          <RefreshCw className="w-6 h-6 text-white/30 animate-spin mx-auto mb-3" />
          <p className="text-sm text-white/40">Loading sessions...</p>
        </div>
      )}

      {/* Active Sessions */}
      {activeSessions.length > 0 && (
        <div className="space-y-3">
          <AnimatePresence mode="popLayout">
            {activeSessions.map((session, index) => (
              <motion.div
                key={session.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ delay: index * 0.05 }}
                className={`session-row flex items-center justify-between p-4 border transition-colors duration-300 ${
                  index === 0
                    ? 'border-l-2 border-l-white/30 border-white/10'
                    : 'border-white/5 hover:border-white/10'
                }`}
                data-testid={`session-row-${session.id}`}
              >
                <div className="flex items-center gap-4">
                  {getDeviceIcon()}
                  <div>
                    <p className="text-sm text-white/80 font-light">
                      {index === 0 ? 'Current session' : `Session ${session.id.slice(0, 8)}`}
                    </p>
                    <p className="text-xs text-white/30 font-light">
                      Created {formatRelativeTime(session.created_at)}
                      {session.expires_at && (
                        <> &middot; Expires {formatRelativeTime(session.expires_at)}</>
                      )}
                    </p>
                  </div>
                </div>

                {index !== 0 && (
                  <button
                    onClick={() => setConfirmRevoke(session.id)}
                    disabled={!!busyId}
                    className="revoke-btn p-2 text-white/30 hover:text-red-400 transition-colors duration-300 disabled:opacity-50"
                    aria-label="Revoke session"
                    data-testid={`revoke-btn-${session.id}`}
                  >
                    {busyId === session.id ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                  </button>
                )}

                {index === 0 && (
                  <span className="text-xs text-white/40 px-2 py-1 border border-white/10">
                    Active now
                  </span>
                )}
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}

      {/* No sessions */}
      {!loading && activeSessions.length === 0 && (
        <div className="text-center py-8 text-white/30">
          <Smartphone className="w-8 h-8 mx-auto mb-3 opacity-50" />
          <p className="text-sm">No active sessions found</p>
        </div>
      )}

      {/* Revoked Sessions (collapsed) */}
      {revokedSessions.length > 0 && (
        <details className="text-white/30">
          <summary className="cursor-pointer text-xs uppercase tracking-widest hover:text-white/50 transition-colors">
            {revokedSessions.length} revoked session{revokedSessions.length > 1 ? 's' : ''}
          </summary>
          <div className="mt-3 space-y-2 pl-4 border-l border-white/5">
            {revokedSessions.slice(0, 5).map(session => (
              <div key={session.id} className="text-xs text-white/20">
                Session {session.id.slice(0, 8)} &middot; Revoked
              </div>
            ))}
            {revokedSessions.length > 5 && (
              <div className="text-xs text-white/20">
                +{revokedSessions.length - 5} more
              </div>
            )}
          </div>
        </details>
      )}

      {/* Confirmation Modal */}
      <AnimatePresence>
        {confirmRevoke && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
            onClick={() => setConfirmRevoke(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-zinc-900 border border-white/10 p-6 max-w-sm w-full"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-center gap-3 mb-4">
                <AlertTriangle className="w-5 h-5 text-yellow-500" />
                <h3 className="text-lg font-light text-white">
                  {confirmRevoke === 'all-others'
                    ? 'Sign out other devices?'
                    : 'Revoke this session?'}
                </h3>
              </div>

              <p className="text-sm text-white/50 mb-6">
                {confirmRevoke === 'all-others'
                  ? 'All other devices will be signed out immediately. You will remain signed in on this device.'
                  : 'This session will be immediately terminated and the device will need to sign in again.'}
              </p>

              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setConfirmRevoke(null)}
                  className="px-4 py-2 text-sm text-white/50 hover:text-white/80 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    if (confirmRevoke === 'all-others') {
                      revokeOtherSessions()
                    } else {
                      revokeSession(confirmRevoke)
                    }
                  }}
                  disabled={!!busyId}
                  className="px-4 py-2 text-sm bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors disabled:opacity-50"
                  data-testid="confirm-revoke-btn"
                >
                  {busyId ? 'Processing...' : 'Revoke'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
